import logger from '@/lib/logger';
import { createContext, useContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Snackbar, Alert, Button } from '@mui/material';
import { signalRService } from '@/services/signalRService';
import type { TestCompleteUpdate, ChannelClassificationUpdate, ClassificationRetryingUpdate, ClassificationFailedUpdate } from '@/services/signalRService';

interface SignalRContextValue {
  isConnected: boolean;
  connectionError: string | null;
}

const SignalRContext = createContext<SignalRContextValue>({
  isConnected: false,
  connectionError: null,
});

export const useSignalR = () => useContext(SignalRContext);

interface SignalRProviderProps {
  children: ReactNode;
}

export function SignalRProvider({ children }: SignalRProviderProps) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [isConnected, setIsConnected] = useState(false);
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const [notification, setNotification] = useState<{
    open: boolean;
    testId: string;
    completedAt: string;
  }>({ open: false, testId: '', completedAt: '' });
  const [failureNotification, setFailureNotification] = useState<{
    open: boolean;
    testId: string;
    error: string;
  }>({ open: false, testId: '', error: '' });

  useEffect(() => {
    let unsubscribeComplete: (() => void) | null = null;
    let unsubscribeChannel: (() => void) | null = null;
    let unsubscribeRetrying: (() => void) | null = null;
    let unsubscribeFailed: (() => void) | null = null;
    let isSubscribed = true;

    const setupSignalR = async () => {
      try {
        await signalRService.connect();
        
        // Check if we're still subscribed (not unmounted during connection)
        if (!isSubscribed) {
          return;
        }
        
        setIsConnected(true);
        setConnectionError(null);

        // Subscribe to global test completion events
        unsubscribeComplete = signalRService.subscribeToAllTestCompletions(
          (update: TestCompleteUpdate) => {
            logger.debug('Test classified globally:', update);

            // Show notification
            setNotification({
              open: true,
              testId: update.testId,
              completedAt: update.completedAt,
            });

            // Invalidate queries to refresh data across the app
            queryClient.invalidateQueries({ queryKey: ['tests'] });
            queryClient.invalidateQueries({ queryKey: ['dashboard'] });
            queryClient.invalidateQueries({ queryKey: ['test', update.testId] });
          }
        );

        // Subscribe to channel classification updates for real-time dashboard updates
        // This allows the dashboard to show pathogen results as they arrive, not just when complete
        unsubscribeChannel = signalRService.subscribeToAllChannelClassifications?.(
          (update: ChannelClassificationUpdate) => {
            logger.debug('Channel classified globally:', update);

            // Invalidate dashboard to refresh pathogen chips in real-time
            queryClient.invalidateQueries({ queryKey: ['dashboard'] });
            queryClient.invalidateQueries({ queryKey: ['tests'] });
            queryClient.invalidateQueries({ queryKey: ['test', update.testId] });
          }
        );

        // Subscribe to classification retry events (intermediate failures)
        unsubscribeRetrying = signalRService.subscribeToClassificationRetrying(
          (update: ClassificationRetryingUpdate) => {
            logger.debug('Classification retrying globally:', update);

            // Silently invalidate queries so tests/dashboard reflect retry state
            queryClient.invalidateQueries({ queryKey: ['tests'] });
            queryClient.invalidateQueries({ queryKey: ['dashboard'] });
            queryClient.invalidateQueries({ queryKey: ['test', update.testId] });
          }
        );

        // Subscribe to classification permanent failure events
        unsubscribeFailed = signalRService.subscribeToClassificationFailed(
          (update: ClassificationFailedUpdate) => {
            logger.warn('Classification failed globally:', update);

            // Show failure notification
            setFailureNotification({
              open: true,
              testId: update.testId,
              error: update.error,
            });

            // Invalidate queries so tests page shows updated status & re-trigger button
            queryClient.invalidateQueries({ queryKey: ['tests'] });
            queryClient.invalidateQueries({ queryKey: ['dashboard'] });
            queryClient.invalidateQueries({ queryKey: ['test', update.testId] });
          }
        );

        logger.debug('SignalR global subscription established');
      } catch (error) {
        if (!isSubscribed) {
          return;
        }
        logger.error('Failed to setup SignalR:', error);
        setIsConnected(false);
        setConnectionError(error instanceof Error ? error.message : 'Connection failed');
      }
    };

    setupSignalR();

    return () => {
      isSubscribed = false;
      unsubscribeComplete?.();
      unsubscribeChannel?.();
      unsubscribeRetrying?.();
      unsubscribeFailed?.();
      // Note: We don't disconnect here because other parts of the app might be using SignalR
      // SignalR will automatically reconnect if needed
    };
  }, [queryClient]);

  const handleCloseNotification = () => {
    setNotification({ ...notification, open: false });
  };

  const handleCloseFailureNotification = () => {
    setFailureNotification({ ...failureNotification, open: false });
  };

  const handleViewResults = () => {
    navigate(`/tests`); // Navigate to tests page, could be /tests/${notification.testId} if detail page exists
    handleCloseNotification();
  };

  return (
    <SignalRContext.Provider value={{ isConnected, connectionError }}>
      {children}
      
      {/* Global notification for test completions */}
      <Snackbar
        open={notification.open}
        autoHideDuration={10000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity="success"
          sx={{ width: '100%' }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={handleViewResults}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              View Results
            </Button>
          }
        >
          Test {notification.testId.substring(0, 8)}... has been classified
        </Alert>
      </Snackbar>

      {/* Global notification for classification failures */}
      <Snackbar
        open={failureNotification.open}
        autoHideDuration={15000}
        onClose={handleCloseFailureNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseFailureNotification}
          severity="error"
          sx={{ width: '100%' }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => {
                navigate('/tests');
                handleCloseFailureNotification();
              }}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              View Test
            </Button>
          }
        >
          Classification failed for test {failureNotification.testId.substring(0, 8)}... — you can re-trigger it from the Tests page.
        </Alert>
      </Snackbar>
    </SignalRContext.Provider>
  );
}
